import React, { useState, useEffect } from 'react';
import {
  Stack,
  Text,
  PrimaryButton,
  DefaultButton,
  MessageBar,
  MessageBarType,
  Spinner,
  Dropdown,
  IDropdownOption,
  TextField,
  Label,
  Separator,
  Icon,
  IconButton,
  Link,
} from '@fluentui/react';
import { IApprovalFlow, IApprovalStep } from '../../shared/models/IApprovalFlow';
import { IApprovalMatrixEntryUI } from '../../shared/models/IApprovalMatrix';
import { approvalFlowApi, approvalMatrixApi } from '../services/api';
import { useAuth } from '../context/AuthContext';

interface IFreigabeFlowZuordnungProps {
  bestellungId: number;
  positionId: number;
  flowType: 'Position';
  currentDepartment?: string;
  onFlowChange?: () => void;
}

export const FreigabeFlowZuordnung: React.FC<IFreigabeFlowZuordnungProps> = ({
  bestellungId,
  positionId,
  onFlowChange,
}) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [flows, setFlows] = useState<IApprovalFlow[]>([]);
  const [departments, setDepartments] = useState<IApprovalMatrixEntryUI[]>([]);
  const [selectedDepartment, setSelectedDepartment] = useState<string>('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [comments, setComments] = useState<{ [flowId: number]: string }>({});
  const [actionInProgress, setActionInProgress] = useState<number | null>(null);

  useEffect(() => {
    loadDepartments();
    if (positionId) {
      loadFlows();
    }
  }, [bestellungId, positionId]);

  const loadDepartments = async () => {
    try {
      const data = await approvalMatrixApi.getAll();
      setDepartments(data);
    } catch (err: any) {
      console.error('Fehler beim Laden der Abteilungen:', err);
    }
  };

  const loadFlows = async () => {
    if (!positionId) return;
    try {
      setLoading(true);
      const allFlows = await approvalFlowApi.getFlowsForBestellung(bestellungId);
      const posFlows = allFlows.filter(f => f.FlowType === 'Position' && f.PositionId === positionId);
      setFlows(posFlows);
    } catch (err: any) {
      setError(err.message || 'Fehler beim Laden der Flows');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFlow = async () => {
    if (!selectedDepartment || !positionId) return;

    try {
      setLoading(true);
      setError(null);
      const newFlow = await approvalFlowApi.createFlowForPosition(
        positionId,
        bestellungId,
        selectedDepartment,
        user?.email
      );
      setFlows(prev => [...prev, newFlow]);
      setSelectedDepartment('');
      setShowAddForm(false);
      if (onFlowChange) onFlowChange();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Fehler beim Erstellen des Flows');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteFlow = async (flowId: number) => {
    if (!confirm('Möchten Sie den Genehmigungsflow wirklich löschen?')) return;

    try {
      setActionInProgress(flowId);
      setError(null);
      await approvalFlowApi.deleteFlow(flowId);
      setFlows(prev => prev.filter(f => f.Id !== flowId));
      if (onFlowChange) onFlowChange();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Fehler beim Löschen des Flows');
    } finally {
      setActionInProgress(null);
    }
  };

  const handleApproveStep = async (flowId: number, stepEmail: string) => {
    try {
      setActionInProgress(flowId);
      setError(null);
      const updatedFlow = await approvalFlowApi.approveStep(flowId, stepEmail, comments[flowId]);
      setFlows(prev => prev.map(f => f.Id === flowId ? updatedFlow : f));
      setComments(prev => ({ ...prev, [flowId]: '' }));
      if (onFlowChange) onFlowChange();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Fehler beim Genehmigen');
    } finally {
      setActionInProgress(null);
    }
  };

  const handleRejectStep = async (flowId: number, stepEmail: string) => {
    if (!comments[flowId]) {
      setError('Bitte geben Sie einen Ablehnungsgrund an');
      return;
    }
    try {
      setActionInProgress(flowId);
      setError(null);
      const updatedFlow = await approvalFlowApi.rejectStep(flowId, stepEmail, comments[flowId]);
      setFlows(prev => prev.map(f => f.Id === flowId ? updatedFlow : f));
      setComments(prev => ({ ...prev, [flowId]: '' }));
      if (onFlowChange) onFlowChange();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Fehler beim Ablehnen');
    } finally {
      setActionInProgress(null);
    }
  };

  const isAdminOrEinkauf = !!(user?.permissions?.isAdmin || user?.permissions?.isEinkauf);

  const direktfreigabeFlow = flows.find(f => f.DepartmentName === 'Direktfreigabe' && f.OverallStatus === 'Freigegeben');
  const normalFlows = flows.filter(f => f.DepartmentName !== 'Direktfreigabe');

  const handleDirektfreigabe = async () => {
    if (!positionId) return;
    try {
      setLoading(true);
      setError(null);
      const newFlow = await approvalFlowApi.createDirektfreigabe(positionId, bestellungId);
      setFlows(prev => [...prev, newFlow]);
      if (onFlowChange) onFlowChange();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Fehler bei Direktfreigabe');
    } finally {
      setLoading(false);
    }
  };

  const handleAufhebenDirektfreigabe = async () => {
    if (!direktfreigabeFlow?.Id) return;
    if (!confirm('Direktfreigabe wirklich aufheben? Die normalen Freigabe-Flows werden wieder wirksam.')) return;
    try {
      setActionInProgress(direktfreigabeFlow.Id);
      setError(null);
      await approvalFlowApi.deleteFlow(direktfreigabeFlow.Id);
      setFlows(prev => prev.filter(f => f.Id !== direktfreigabeFlow.Id));
      if (onFlowChange) onFlowChange();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Fehler beim Aufheben der Direktfreigabe');
    } finally {
      setActionInProgress(null);
    }
  };

  const canUserApprove = (step: IApprovalStep): boolean =>
    step.ApproverEmail.toLowerCase() === (user?.email || '').toLowerCase() && step.Status === 'Offen';

  const getStatusIcon = (status: IApprovalStep['Status'] | IApprovalFlow['OverallStatus']) => {
    switch (status) {
      case 'Freigegeben': return { iconName: 'CompletedSolid', color: '#107c10' };
      case 'Abgelehnt': return { iconName: 'StatusErrorFull', color: '#d13438' };
      case 'Teilweise freigegeben': return { iconName: 'SkypeCircleCheck', color: '#ff8c00' };
      case 'Offen': return { iconName: 'Clock', color: '#605e5c' };
      default: return { iconName: 'Help', color: '#605e5c' };
    }
  };

  const getOverallStatusColor = (status: IApprovalFlow['OverallStatus']) => {
    switch (status) {
      case 'Freigegeben': return '#107c10';
      case 'Abgelehnt': return '#d13438';
      case 'Teilweise freigegeben': return '#ff8c00';
      default: return '#605e5c';
    }
  };

  const departmentOptions: IDropdownOption[] = departments.map(d => ({ key: d.Department, text: d.Department }));

  const renderStepRow = (step: IApprovalStep, idx: number) => {
    const si = getStatusIcon(step.Status);
    return (
      <Stack key={idx} horizontal tokens={{ childrenGap: 8 }} verticalAlign="center"
        styles={{ root: { padding: '6px 10px', backgroundColor: '#faf9f8', borderRadius: 4, border: '1px solid #edebe9' } }}
      >
        <Icon iconName={si.iconName} styles={{ root: { fontSize: 14, color: si.color } }} />
        <Stack styles={{ root: { flexGrow: 1 } }}>
          <Text variant="small" styles={{ root: { fontWeight: 500 } }}>{step.ApproverName}</Text>
          {step.Comments && <Text variant="small" styles={{ root: { color: '#605e5c', fontStyle: 'italic' } }}>Kommentar: {step.Comments}</Text>}
          {step.ApprovedDate && <Text variant="small" styles={{ root: { color: '#605e5c' } }}>{new Date(step.ApprovedDate).toLocaleString('de-DE')}</Text>}
        </Stack>
        <Text variant="small" styles={{ root: { color: si.color, fontWeight: 600 } }}>{step.Status}</Text>
      </Stack>
    );
  };

  const renderFlow = (flow: IApprovalFlow) => {
    const deptSteps = flow.Steps.filter(s => s.StepType === 'Department');
    const mgmtSteps = flow.Steps.filter(s => s.StepType === 'Management');
    const statusColor = getOverallStatusColor(flow.OverallStatus);

    // Offener Schritt des aktuellen Users in diesem Flow
    const userOpenStep = flow.Steps.find(s => canUserApprove(s));

    return (
      <Stack
        key={flow.Id}
        tokens={{ childrenGap: 10 }}
        styles={{ root: { padding: 12, backgroundColor: '#f3f2f1', borderRadius: 4, border: `2px solid ${statusColor}` } }}
      >
        {/* Flow Header */}
        <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
          <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
            <Icon iconName={getStatusIcon(flow.OverallStatus as any).iconName} styles={{ root: { fontSize: 18, color: statusColor } }} />
            <Text variant="medium" styles={{ root: { fontWeight: 600 } }}>{flow.DepartmentName}</Text>
          </Stack>
          <Stack horizontal tokens={{ childrenGap: 6 }} verticalAlign="center">
            <Text variant="small" styles={{ root: { color: statusColor, fontWeight: 600 } }}>{flow.OverallStatus}</Text>
            {(user?.permissions?.isAdmin || user?.permissions?.isEinkauf) && (
              <IconButton
                iconProps={{ iconName: 'Delete' }}
                title="Flow entfernen"
                styles={{ root: { color: '#d13438', height: 24 } }}
                onClick={() => handleDeleteFlow(flow.Id!)}
                disabled={actionInProgress !== null}
              />
            )}
          </Stack>
        </Stack>

        {/* Aktions-Bereich oben — nur für den aktuell angemeldeten Genehmiger */}
        {userOpenStep && (
          <Stack tokens={{ childrenGap: 8 }}>
            <TextField
              placeholder="Kommentar (optional für Genehmigung, erforderlich für Ablehnung)"
              value={comments[flow.Id!] || ''}
              onChange={(_, v) => setComments(prev => ({ ...prev, [flow.Id!]: v || '' }))}
              multiline rows={2}
            />
            <Stack horizontal tokens={{ childrenGap: 8 }}>
              <PrimaryButton text="Freigeben" iconProps={{ iconName: 'Accept' }} onClick={() => handleApproveStep(flow.Id!, userOpenStep.ApproverEmail)} disabled={actionInProgress !== null} />
              <DefaultButton text="Ablehnen" iconProps={{ iconName: 'Cancel' }} onClick={() => handleRejectStep(flow.Id!, userOpenStep.ApproverEmail)} disabled={actionInProgress !== null} />
            </Stack>
            <Separator />
          </Stack>
        )}

        {/* Abteilungsgenehmiger */}
        {deptSteps.length > 0 && (
          <Stack tokens={{ childrenGap: 6 }}>
            <Label styles={{ root: { fontSize: 12 } }}>Abteilungsgenehmiger</Label>
            {deptSteps.map((step, idx) => renderStepRow(step, idx))}
          </Stack>
        )}

        {/* Geschäftsführung */}
        {mgmtSteps.length > 0 && (
          <Stack tokens={{ childrenGap: 6 }}>
            <Label styles={{ root: { fontSize: 12 } }}>Geschäftsführung</Label>
            {mgmtSteps.map((step, idx) => renderStepRow(step, idx))}
          </Stack>
        )}
      </Stack>
    );
  };

  if (loading && flows.length === 0) {
    return <Spinner label="Lade Freigabe-Flows..." />;
  }

  return (
    <Stack tokens={{ childrenGap: 12 }}>
      {error && (
        <MessageBar messageBarType={MessageBarType.error} onDismiss={() => setError(null)}>
          {error}
        </MessageBar>
      )}

      {/* ── Direktfreigabe (nur Admin/Einkauf) ── */}
      {isAdminOrEinkauf && (
        <Stack
          tokens={{ childrenGap: 8 }}
          styles={{ root: {
            padding: 12,
            borderRadius: 4,
            border: direktfreigabeFlow ? '2px solid #107c10' : '2px dashed #0078d4',
            backgroundColor: direktfreigabeFlow ? '#e6f4ea' : '#f0f6ff',
          }}}
        >
          <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
            <Icon
              iconName={direktfreigabeFlow ? 'SkypeCircleCheck' : 'LightningBolt'}
              styles={{ root: { fontSize: 18, color: direktfreigabeFlow ? '#107c10' : '#0078d4' } }}
            />
            <Text variant="medium" styles={{ root: { fontWeight: 600, color: direktfreigabeFlow ? '#107c10' : '#0078d4' } }}>
              {direktfreigabeFlow ? 'Direktfreigabe aktiv' : 'Direktfreigabe'}
            </Text>
          </Stack>

          {direktfreigabeFlow ? (
            <Stack tokens={{ childrenGap: 6 }}>
              {direktfreigabeFlow.Steps.map((step, idx) => (
                <Text key={idx} variant="small" styles={{ root: { color: '#107c10' } }}>
                  Freigegeben von {step.ApproverName}
                  {step.ApprovedDate && ` am ${new Date(step.ApprovedDate).toLocaleString('de-DE')}`}
                </Text>
              ))}
              <Text variant="small" styles={{ root: { color: '#605e5c', fontStyle: 'italic' } }}>
                Alle anderen Freigabe-Flows dieser Position sind außer Kraft gesetzt.
              </Text>
              <DefaultButton
                text="Direktfreigabe aufheben"
                iconProps={{ iconName: 'Cancel' }}
                onClick={handleAufhebenDirektfreigabe}
                disabled={actionInProgress !== null || loading}
                styles={{ root: { marginTop: 4, color: '#d13438', borderColor: '#d13438' } }}
              />
            </Stack>
          ) : (
            <Stack tokens={{ childrenGap: 6 }}>
              <Text variant="small" styles={{ root: { color: '#605e5c' } }}>
                Setzt alle anderen Freigabe-Flows außer Kraft und genehmigt die Position sofort.
              </Text>
              <PrimaryButton
                text="Direkt freigeben"
                iconProps={{ iconName: 'LightningBolt' }}
                onClick={handleDirektfreigabe}
                disabled={loading || actionInProgress !== null}
                styles={{ root: { width: 'fit-content' } }}
              />
            </Stack>
          )}
        </Stack>
      )}

      {/* ── Bestehende normale Flows ── */}
      {normalFlows.length > 0 ? (
        <Stack tokens={{ childrenGap: 10 }}>
          {normalFlows.map(renderFlow)}
        </Stack>
      ) : (
        <MessageBar messageBarType={direktfreigabeFlow ? MessageBarType.success : MessageBarType.info}>
          {direktfreigabeFlow
            ? 'Keine weiteren Freigabe-Flows — Direktfreigabe ist aktiv.'
            : 'Für diese Position wurde noch kein Genehmigungsflow erstellt.'}
        </MessageBar>
      )}

      {/* Abteilung hinzufügen */}
      {showAddForm ? (
        <Stack tokens={{ childrenGap: 8 }} styles={{ root: { padding: 12, backgroundColor: '#f3f2f1', borderRadius: 4, border: '1px solid #edebe9' } }}>
          <Text variant="small" styles={{ root: { fontWeight: 600 } }}>Abteilung hinzufügen</Text>
          <Dropdown
            placeholder="Abteilung auswählen"
            options={departmentOptions}
            selectedKey={selectedDepartment}
            onChange={(_, option) => setSelectedDepartment(option?.key as string)}
          />
          <Stack horizontal tokens={{ childrenGap: 8 }}>
            <PrimaryButton
              text="Flow erstellen"
              iconProps={{ iconName: 'Add' }}
              onClick={handleCreateFlow}
              disabled={!selectedDepartment || loading}
            />
            <DefaultButton text="Abbrechen" onClick={() => { setShowAddForm(false); setSelectedDepartment(''); }} />
          </Stack>
        </Stack>
      ) : (
        <Link onClick={() => setShowAddForm(true)} styles={{ root: { fontSize: 13 } }}>
          + Abteilung hinzufügen
        </Link>
      )}
    </Stack>
  );
};
